import { getApiErrorMessage } from "@/api/utils";
import { Button } from "@/components/ui/button";
import { ROUTES } from "@/constants/routes";
import { useCanvasCreation } from "@/context/CanvasCreationContext";
import { route } from "@/utils/route";
import { ErrorDisplay, Spinner } from "@cohesive/ui-components";
import { useEffect } from "react";
import { Navigate } from "react-router-dom";

/**
 * The shell "New canvas" opens immediately. Renders the same loader the canvas
 * page shows while its room connects, so the click has somewhere to land while
 * the create request runs. No canvas id exists here — nothing may touch the
 * Liveblocks room from this route.
 *
 * This route owns where it goes next. The create handler only records the
 * outcome: a phase change and a router navigation issued together can commit
 * in either order, so anything that navigated from there would leave this page
 * rendering one frame against a phase that no longer describes it. The only
 * effect here reports that the outcome was shown; the provider clears it once
 * the route has moved on from that.
 */
export function CanvasCreatingPage() {
  const {
    phase,
    retryCanvasCreation,
    dismissCanvasCreation,
    markCanvasCreationShown,
  } = useCanvasCreation();

  // Idempotent, so StrictMode's double effect is harmless.
  const settled = phase.status === "created" || phase.status === "error";
  useEffect(() => {
    if (settled) markCanvasCreationShown();
  }, [settled, markCanvasCreationShown]);

  if (phase.status === "created") {
    // Replace so Back skips the shell it was never a destination for.
    return (
      <Navigate
        to={route(ROUTES.CANVAS.DETAIL, { canvasId: phase.canvasId })}
        replace
      />
    );
  }

  if (phase.status === "error") {
    return (
      <div className="h-full w-full min-h-0 bg-background">
        <ErrorDisplay
          title="Could not create canvas"
          message={getApiErrorMessage(phase.error)}
          footer={
            <div className="flex items-center justify-center gap-2">
              <Button type="button" onClick={retryCanvasCreation}>
                Retry
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={dismissCanvasCreation}
              >
                Back
              </Button>
            </div>
          }
        />
      </div>
    );
  }

  // Reloaded onto the shell, or landed on it directly: there is no create to
  // wait for and the pending tab did not survive the reload.
  if (phase.status === "idle") {
    return <Navigate to={ROUTES.CANVAS.ROOT} replace />;
  }

  return (
    <div className="flex h-full w-full items-center justify-center bg-background">
      <Spinner size="lg" />
    </div>
  );
}
